import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { LineChart } from "./LineChart";
import { DeltaBadge } from "./DeltaBadge";
import { apiFetch } from "../lib/api";

type Series = { d: string; v: number }[];
type Metric = {
  current: number;
  previous: number;
  deltaPct: number | null;
  series: Series;
};
type Overview = {
  period: { key: string; days: number; since: string; until: string };
  metrics: Record<string, Metric>;
};

interface Props {
  /** Metric key returned by /api/admin/overview. */
  metric: string;
  /** Heading above the chart. */
  title?: string;
  /** Formatter for the headline value. */
  format?: (n: number) => string;
  /** Color for the line. */
  color?: string;
  /** Chart height. */
  height?: number;
}

/**
 * Mini-dashboard block shown above an admin list page. Fetches
 * /api/admin/overview?period=30d, plucks one metric, and renders:
 *   • headline value
 *   • period-over-period delta badge
 *   • 30-day daily line chart
 */
export function OverviewMetricChart({
  metric,
  title,
  format = (n) => n.toLocaleString("pt-BR"),
  color = "var(--color-accent)",
  height = 200,
}: Props) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch("/api/admin/overview?period=30d")
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (alive) setData(d as Overview);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
        Não foi possível carregar o gráfico.
      </div>
    );
  }
  if (data === null) {
    return (
      <div
        className="grid place-items-center rounded-2xl border border-border bg-surface"
        style={{ height }}
      >
        <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
      </div>
    );
  }
  const m = data.metrics[metric];
  if (!m) return null;

  return (
    <div>
      {title && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.2em] text-text-tertiary">
            {title}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold tracking-tight text-text">
              {format(m.current)}
            </span>
            <DeltaBadge pct={m.deltaPct} />
          </div>
        </div>
      )}
      <LineChart data={m.series} color={color} height={height} />
    </div>
  );
}
